/**
 * The base skeleton placeholder shape and its shared-wave engine.
 *
 * `BaseSkeleton` renders one token-colored `div` in every state. While
 * loading (and absent a reduced-motion preference) it joins the shimmer: a
 * bell-shaped band crossing every mounted surface as one continuous
 * wave. Each surface runs its own web animation from `startTime` 0 on the
 * shared document timeline, with the wave offset baked into its keyframes by
 * page-x — so mount timing cannot desynchronize shapes, and no shared paint
 * surface is involved (`background-attachment: fixed` re-anchors inside
 * transformed ancestors, and RN-web ScrollViews carry an identity transform).
 *
 * The wave sizes its travel to the collective extent of the mounted surfaces
 * — a lone small placeholder gets a tight, frequent wave; a full loading
 * screen a page-wide one — and updates on any reflow via one body-level
 * ResizeObserver. The Storybook test runner emulates `prefers-reduced-motion`,
 * so under it the surfaces render static and captures don't depend on timing.
 */

import { useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'react-native-reanimated';

import styles from './base-skeleton.module.css';

// The bell is half a viewport wide and takes SHIMMER_PERIOD_MS to sweep one
// viewport end to end (enter at the left edge, clear the right). That fixes
// its speed; each wave's own travel then sets its duration. The width is
// published to the stylesheet as a CSS variable, so it is defined only here.
const SHIMMER_BELL_VW = 50;
const SHIMMER_PERIOD_MS = 2000;

// The one rule the surface hoists into <head>: it hands the bell width to
// the stylesheet, so the module's background-size and update()'s math read
// one constant. `href` and `precedence` together are what make React 19
// treat the <style> as a hoistable stylesheet rather than inline markup:
// `href` is the identity it dedupes on (every surface renders the element,
// one lands in <head>), and `precedence` names the group it is inserted
// into, so the sheet's position in the cascade relative to other hoisted
// sheets is stable and not dependent on which component rendered first.
const SHIMMER_BELL_STYLE_HREF = 'psi-shimmer-bell';
const SHIMMER_BELL_CSS = `:root { --psi-shimmer-bell-width: ${SHIMMER_BELL_VW}vw; }`;

type Extent = { animation: Animation; left: number; right: number };

/**
 * ShimmerWave: a document-wide loading animation wave — all of the imperative
 * work (WAAPI, layout measurement, the body-level ResizeObserver) lives behind
 * this abstraction. `register(el)` starts an element's animation and registers
 * it; the returned function unregisters. On every (un)enrollment or reflow,
 * `update` measures the collective page-x extent and re-points every animation
 * at the shared wave.
 */
class ShimmerWave {
    private readonly animations = new Map<HTMLElement, Animation>();
    private resizeObserver: ResizeObserver | undefined;

    register(el: HTMLElement) {
        if (typeof el.animate !== 'function') return;
        // WAAPI rather than CSS @keyframes: each animation is pinned to
        // startTime 0, so its phase depends on document time alone (a surface
        // that mounts later still lands in step), and update() re-points
        // keyframes and duration without restarting anything. The overall
        // effect is many skeleton shapes taking part in one shimmer, rather
        // than many independent local animations.
        const animation = el.animate(null, { duration: SHIMMER_PERIOD_MS, iterations: Number.POSITIVE_INFINITY });
        animation.startTime = 0;
        this.animations.set(el, animation);
        this.observeReflows();
        this.update();
        return () => this.unregister(el);
    }

    private unregister(el: HTMLElement) {
        const animation = this.animations.get(el);
        if (!animation) return;
        this.animations.delete(el);
        animation.cancel();
        if (this.animations.size === 0) {
            this.resizeObserver?.disconnect();
            this.resizeObserver = undefined;
        } else {
            this.update();
        }
    }

    /**
     * One observer on the body for as long as anything is enrolled. It
     * watches the body because ResizeObserver reports size, not position:
     * per-shape observation would miss reflows that move a fixed-size shape
     * sideways, while any layout change that moves a shape also resizes some
     * ancestor.
     */
    private observeReflows() {
        if (this.resizeObserver || typeof ResizeObserver !== 'function') return;
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(document.body);
    }

    /** Page-x extents of the enrolled surfaces that currently have layout. */
    private measure(): Extent[] {
        const extents: Extent[] = [];
        for (const [el, animation] of this.animations) {
            const { left, width } = el.getBoundingClientRect();
            if (width === 0) continue;
            const pageLeft = left + window.scrollX;
            extents.push({ animation, left: pageLeft, right: pageLeft + width });
        }
        return extents;
    }

    /**
     * Synchronous and deliberately unbatched: a commit mounting N surfaces
     * updates N times, but layout effects run after all of the commit's DOM
     * mutations, so every pass sees final geometry, the last pass trues up
     * the whole set before paint, and the total cost is a few milliseconds
     * once per loading screen.
     */
    private update() {
        const extents = this.measure();
        if (extents.length === 0) return;
        const bellPx = (SHIMMER_BELL_VW / 100) * window.innerWidth;
        const start = Math.min(...extents.map((e) => e.left)) - bellPx;
        const end = Math.max(...extents.map((e) => e.right));
        const travelPx = end - start;
        const durationMs = (travelPx / (window.innerWidth + bellPx)) * SHIMMER_PERIOD_MS;
        for (const { animation, left } of extents) retarget({ animation, fromPx: start - left, travelPx, durationMs });
    }
}

/**
 * Re-aims one animation at the shared wave. A shape animates its own
 * `background-position`, which is measured from its own left edge, but the
 * wave is defined in page coordinates — so `fromPx` is the wave's start
 * translated into this shape's frame (`waveStart − shapeLeft`, usually
 * negative: the bell begins off to the left of the box). Every shape then
 * runs the same travel and duration from `startTime` 0, so at any instant
 * their backgrounds place the bell at the same page position.
 */
function retarget({
    animation,
    fromPx,
    travelPx,
    durationMs,
}: {
    animation: Animation;
    fromPx: number;
    travelPx: number;
    durationMs: number;
}) {
    const effect = animation.effect as KeyframeEffect;
    effect.setKeyframes([{ backgroundPosition: `${fromPx}px center` }, { backgroundPosition: `${fromPx + travelPx}px center` }]);
    effect.updateTiming({ duration: durationMs });
    animation.startTime = 0;
}

// One instance at module scope on purpose: the wave is a document-scoped
// concern (unrelated subtrees share it), and in a bundle, module scope is
// document scope.
const shimmerWave = new ShimmerWave();

type BaseSkeletonProps = {
    width: number | string;
    height: number;
    borderRadius: number;
    marginRight?: number;
    color?: string;
    isLoading?: boolean;
    fade?: boolean;
};

/**
 * A skeleton placeholder shape: one `div` in every state. The bar is the
 * `colorSkeletonPrimary` token unless `color` overrides it, and the wave
 * fades it toward `colorSkeletonSecondary` — a design-owned pair, tuned per
 * theme rather than derived. While animating, the surface joins the shared
 * wave; a static surface carries no shimmer class, so nothing is painted
 * beyond the flat bar.
 *
 * `isLoading` gates only the loading affordance (the shimmer) — the shapes
 * always render; unlike some libraries' Skeleton `loading` prop, `false`
 * never swaps in children. Renders static when the caller passes
 * `isLoading={false}` (decorative or empty-state usage) or the user prefers
 * reduced motion.
 *
 * `fade` masks the shape from full strength at the left to a fifth at the
 * right, the way the designs draw text bars.
 */
export function BaseSkeleton({ color, isLoading = true, fade = false, ...shape }: BaseSkeletonProps) {
    const reducedMotion = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const animate = isLoading && !reducedMotion;

    useLayoutEffect(() => {
        const el = ref.current;
        if (!animate || !el) return;
        return shimmerWave.register(el);
    }, [animate]);

    return (
        <>
            <style href={SHIMMER_BELL_STYLE_HREF} precedence='medium'>
                {SHIMMER_BELL_CSS}
            </style>
            <div
                className={[styles.shape, animate && styles.shimmer, fade && styles.fade].filter(Boolean).join(' ')}
                ref={ref}
                style={{ ...shape, backgroundColor: color }}
            />
        </>
    );
}
